// journalDesign.jsx — shared "Apple-table" design system for the trading
// journal (Playbook page + Investments dashboard). Single source of truth
// for the monochrome ink/gray + green/red tokens, the setup-grade badge,
// the activity ring, and the pill controls. Light palette is the reference
// mockup's :root verbatim; dark is the same monochrome approach on an
// ink-dark ground (status colors brightened, never any blue).

import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

export const SYSTEM_STACK =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", Inter, "Segoe UI", Roboto, sans-serif';

export const makeTokens = (isDark) => isDark ? {
  bg: '#1C1C1E',
  card: '#242426', ink: '#F5F5F7', inkSoft: '#A1A1A6', muted: '#86868B',
  line: '#38383A', graphite: '#2E2E30', green: '#30D158', red: '#FF453A',
  greenTint: 'rgba(48,209,88,0.14)', redTint: 'rgba(255,69,58,0.16)',
  segTrack: '#2E2E30', segOn: '#48484A', segOnText: '#F5F5F7',
  btnSecBg: '#2E2E30', btnSecText: '#F5F5F7', btnSecHover: '#3A3A3C',
  btnPriBg: '#F5F5F7', btnPriText: '#1A1A1C', btnPriHover: '#FFFFFF',
  ringTrack: '#38383A',
  shadow: '0 1px 2px rgba(0,0,0,0.45), 0 6px 20px rgba(0,0,0,0.35)',
  gradeSolidBg: '#F5F5F7', gradeSolidText: '#1A1A1C',
  gradeAltBg: '#AEAEB2', gradeAltText: '#1A1A1C',
} : {
  bg: '#F5F5F7',
  card: '#FFFFFF', ink: '#1D1D1F', inkSoft: '#6E6E73', muted: '#86868B',
  line: '#E5E5EA', graphite: '#F0F0F2', green: '#248A3D', red: '#D70015',
  greenTint: '#EAFAEE', redTint: '#FFEFEE',
  segTrack: '#E9E9EC', segOn: '#FFFFFF', segOnText: '#1D1D1F',
  btnSecBg: '#E9E9EC', btnSecText: '#1D1D1F', btnSecHover: '#DEDEE2',
  btnPriBg: '#1D1D1F', btnPriText: '#FFFFFF', btnPriHover: '#000000',
  ringTrack: '#EDEDF0',
  shadow: '0 1px 2px rgba(0,0,0,0.04), 0 6px 20px rgba(0,0,0,0.03)',
  gradeSolidBg: '#1D1D1F', gradeSolidText: '#FFFFFF',
  gradeAltBg: '#48484A', gradeAltText: '#FFFFFF',
};

// ── Time-range control (shared) ──────────────────────────────────────────────
// Client-side only: every widget filters its already-fetched trade set by exit
// day, so a range change never refetches. Extracted from InvestmentsGrid so the
// Insights page shares one definition.
export const isoLocal = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const TIME_RANGES = [
  { key: 'all',   label: 'All time' },
  { key: 'today', label: 'Today' },
  { key: 'week',  label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: '30d',   label: 'Last 30 days' },
  { key: '90d',   label: 'Last 90 days' },
  { key: '365d',  label: 'Last 365 days' },
];

export function resolveRange(key, now = new Date()) {
  if (!key || key === 'all') return null;
  const end = isoLocal(now);
  if (key === 'today') return { start: end, end };
  if (key === 'week') {
    const d = new Date(now); const dow = (d.getDay() + 6) % 7; // Mon=0
    d.setDate(d.getDate() - dow); return { start: isoLocal(d), end };
  }
  if (key === 'month') return { start: isoLocal(new Date(now.getFullYear(), now.getMonth(), 1)), end };
  const days = { '30d': 30, '90d': 90, '365d': 365 }[key] || 30;
  const d = new Date(now); d.setDate(d.getDate() - (days - 1));
  return { start: isoLocal(d), end };
}

export const tradeExitDay = (t) =>
  String(t.exit_date || t.exit_datetime || t.entry_date || t.entry_datetime || '').slice(0, 10);

export const inRange = (t, range) => {
  if (!range) return true;
  const d = tradeExitDay(t);
  return d && d >= range.start && d <= range.end;
};

// The equal-length window immediately before `range` (for "vs. prior period"
// deltas). Null when there is no bounded range.
export const priorRange = (range) => {
  if (!range || !range.start || !range.end) return null;
  const start = new Date(range.start + 'T00:00:00Z');
  const end = new Date(range.end + 'T00:00:00Z');
  const span = Math.max(1, Math.round((end - start) / 86400000) + 1);
  const prevEnd = new Date(start); prevEnd.setUTCDate(prevEnd.getUTCDate() - 1);
  const prevStart = new Date(prevEnd); prevStart.setUTCDate(prevStart.getUTCDate() - (span - 1));
  return { start: prevStart.toISOString().slice(0, 10), end: prevEnd.toISOString().slice(0, 10) };
};

// Bridges the TIME_RANGES key system (resolveRange's ISO date STRINGS) to the
// older { start, end: Date, type } shape the pre-redesign widgets
// (MainGrid/TradingGrid/StockScanner's dateRange prop) still expect, so the
// shared Header's single Range control can drive both families of consumer.
export function toLegacyDateRange(key) {
  const def = TIME_RANGES.find((r) => r.key === key);
  const range = resolveRange(key);
  if (!range) return { start: null, end: null, type: 'All Time' };
  return {
    start: new Date(`${range.start}T00:00:00`),
    end: new Date(`${range.end}T23:59:59`),
    type: def ? def.label : key,
  };
}

// Thin wrapper: the reference pill segmented control bound to TIME_RANGES.
export function TimeRangePill({ T, value, onChange }) {
  return (
    <Segmented T={T} value={value} onChange={onChange}
      options={TIME_RANGES.map((r) => ({ value: r.key, label: r.label }))} />
  );
}

// Rounded card surface (bg + shadow + radius). Matches the reference .card.
export function Panel({ T, sx, children, pad = '22px 24px' }) {
  return (
    <Box sx={{ bgcolor: T.card, borderRadius: '18px', p: pad, boxShadow: T.shadow, ...sx }}>
      {children}
    </Box>
  );
}

// Small tokened stat card (tilt / KPI / commit-trade) — shared by the
// Insights (Deep Dive) and AI Coach pages.
export function StatCard({ T, label, value, color, minWidth = 200 }) {
  return (
    <Panel T={T} pad="16px 20px" sx={{ flex: 1, minWidth }}>
      <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.muted, textTransform: 'uppercase',
        letterSpacing: '.03em', mb: '10px' }}>{label}</Typography>
      <Typography sx={{ fontSize: 19, fontWeight: 700, color: color || T.ink }}>{value}</Typography>
    </Panel>
  );
}

export const pnlColor = (v) => (v >= 0 ? '#16a34a' : '#dc2626');
export const fmtMoney = (v) =>
  `${v >= 0 ? '+' : ''}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Rounded dollars, no sign — "$160". For budgets/limits where the sign is implied.
export const fmt$ = (n) => `$${Math.abs(Math.round(Number(n) || 0)).toLocaleString()}`;
// "Sep 5" from an ISO date; passthrough on anything unparseable.
export const prettyDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// playbook.rating_text: one tier scale, A+ (best) down to D, plus two
// special states (DO NOT TRADE, Unrated). Order here is display order.
export const GRADE_COLORS = {
  'A+ Setup': '#22c55e', 'A Setup': '#16a34a', 'B Setup': '#65a30d', 'C Setup': '#d97706',
  'D Setup': '#ea580c', 'DO NOT TRADE': '#dc2626', 'Unrated': '#9ca3af',
};
export const RATING_OPTIONS = Object.keys(GRADE_COLORS);
export const deriveRatingColor = (text) => {
  if (/^A/.test(text)) return 'success';
  if (/^B|^C/.test(text)) return 'warning';
  if (/^D|DO NOT TRADE/.test(text)) return 'error';
  return 'grey';
};
export const RATING_COLOR_HEX = { success: '#16a34a', warning: '#d97706', error: '#dc2626', grey: '#9ca3af' };

// playbook.rating_text -> short badge + tier style.
// solid = A+; alt = A; outline = B; muted outline = C / D; solid red = DO NOT TRADE; faint = Unrated.
export const SETUP_GRADE = {
  'A+ Setup':     { text: 'A+',  tier: 'solid' },
  'A Setup':      { text: 'A',   tier: 'alt' },
  'B Setup':      { text: 'B',   tier: 'outline' },
  'C Setup':      { text: 'C',   tier: 'muted' },
  'D Setup':      { text: 'D',   tier: 'muted' },
  'DO NOT TRADE': { text: 'DNT', tier: 'dangerSolid' },
  'Unrated':      { text: '—',   tier: 'none' },
};

export function SetupBadge({ T, rating }) {
  const g = SETUP_GRADE[rating] || SETUP_GRADE['Unrated'];
  const base = {
    fontFamily: 'inherit', fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2,
    px: '12px', py: '5px', borderRadius: '8px', display: 'inline-block',
    letterSpacing: '-0.01em', whiteSpace: 'nowrap',
  };
  const byTier = {
    solid: { bgcolor: T.gradeSolidBg, color: T.gradeSolidText },
    alt: { bgcolor: T.gradeAltBg, color: T.gradeAltText },
    outline: { border: `1.5px solid ${T.line}`, color: T.inkSoft },
    muted: { border: `1.5px solid ${T.line}`, color: T.muted },
    dangerSolid: { bgcolor: T.red, color: '#FFFFFF' },
    none: { border: `1.5px solid ${T.line}`, color: T.muted, opacity: 0.65 },
  };
  return <Box component="span" sx={{ ...base, ...byTier[g.tier] }}>{g.text}</Box>;
}

export const RING_R = 15;
export const RING_CIRC = 2 * Math.PI * RING_R; // ~94.25

// Small win-rate ring: green >=50, red <50, neutral track-only with no trades.
export function WinRateRing({ T, value, hasTrades }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const stroke = !hasTrades ? T.line : (pct >= 50 ? T.green : T.red);
  const offset = hasTrades ? RING_CIRC * (1 - pct / 100) : RING_CIRC;
  return (
    <Box sx={{ position: 'relative', width: 36, height: 36, flex: 'none' }}>
      <svg width="36" height="36" viewBox="0 0 36 36" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="18" cy="18" r={RING_R} fill="none" stroke={T.ringTrack} strokeWidth="4" />
        <circle cx="18" cy="18" r={RING_R} fill="none" stroke={stroke} strokeWidth="4"
          strokeLinecap="round" strokeDasharray={RING_CIRC} strokeDashoffset={offset} />
      </svg>
    </Box>
  );
}

// Reference pill segmented control. Preserves the caller's change handler
// exactly - only the chrome changes.
export function Segmented({ T, value, onChange, options }) {
  return (
    <Box sx={{ display: 'flex', bgcolor: T.segTrack, borderRadius: '9px', p: '2px' }}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <Box component="button" type="button" key={o.value}
            onClick={() => { if (!on) onChange(o.value); }}
            sx={{ font: 'inherit', fontFamily: 'inherit', fontSize: '0.8rem', fontWeight: 500,
              px: '14px', py: '6px', border: 'none', borderRadius: '7px', whiteSpace: 'nowrap',
              cursor: on ? 'default' : 'pointer', transition: 'all .15s ease',
              bgcolor: on ? T.segOn : 'transparent', color: on ? T.segOnText : T.inkSoft,
              boxShadow: on ? '0 1px 2px rgba(0,0,0,0.12)' : 'none' }}>
            {o.label}
          </Box>
        );
      })}
    </Box>
  );
}

export const btnSecondarySx = (T) => ({
  fontFamily: SYSTEM_STACK, fontSize: '0.8rem', fontWeight: 500, textTransform: 'none',
  px: 2, py: '7px', minWidth: 0, borderRadius: '20px', boxShadow: 'none',
  bgcolor: T.btnSecBg, color: T.btnSecText, '&:hover': { bgcolor: T.btnSecHover, boxShadow: 'none' },
});
export const btnPrimarySx = (T) => ({
  fontFamily: SYSTEM_STACK, fontSize: '0.8rem', fontWeight: 600, textTransform: 'none',
  px: 2.25, py: '7px', minWidth: 0, borderRadius: '20px', boxShadow: 'none',
  bgcolor: T.btnPriBg, color: T.btnPriText, '&:hover': { bgcolor: T.btnPriHover, boxShadow: 'none' },
});

// Pill dropdown trigger (matches .filter-select in the reference).
export function FilterPill({ T, label, value, active, onClick }) {
  return (
    <Box component="button" type="button" onClick={onClick}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, font: 'inherit',
        fontFamily: 'inherit', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer',
        px: '14px', py: '7px', borderRadius: '20px', whiteSpace: 'nowrap',
        border: `1px solid ${active ? T.btnPriBg : T.line}`,
        bgcolor: active ? T.btnPriBg : T.card, color: active ? T.btnPriText : T.inkSoft }}>
      <span>{label}{value ? `: ${value}` : ''}</span>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </Box>
  );
}
